import React from "react";
import { IonSelectOption } from "@ionic/react";
import { getCustomerFullAddress } from "../../utils";
import Select from "../Select/Select";
import { useAuth } from "../../contexts/auth-context";
import {
  useCustomerAddressesStateData,
  useCustomerAddressSelectData,
} from "../../providers/CustomerAddresses/CustomerAddressesStateContext";
import { useCustomerAddressesStorageData } from "../../providers/customerAddressesStorage";
import classNames from "classnames/bind";
import styles from "./CurrentShippingAddress.module.scss";
import { ShippingAddressManually } from "../ShippingAddressManually/ShippingAddressManually";
import useCheckAddressForExpressMutation from "../../providers/shippingZones/useGetCheckAddressForExpress";
import { useMarketMode } from "../../providers/marketMode";
import { useConfig } from "../../providers/config";
import { toast } from "react-toastify";
import { Address } from "@core/types";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import { useHistory } from "react-router";
import { useGetCustomerAddresses } from "../../providers/CustomerAddresses";
import { useIsViewFocused } from "../../hooks";
import useGetCurrentAddress from "../../hooks/useGetCurrentAddress";
import { useTranslation } from "react-i18next";
import { useCartData } from "../../providers/cartDataStore";

const cx = classNames.bind(styles);

interface CurrentShippingAddressSelectProps {
  selectColor?: string;
}

const CurrentShippingAddressSelect = ({ selectColor }: CurrentShippingAddressSelectProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isPathnameWithCondition =
    history.location.pathname.includes("shipping") ||
    history.location.pathname.includes("order") ||
    history.location.pathname.includes("cart");
  const { user, isUserAuthenticated } = useAuth();
  const config = useConfig();
  const { mutateAsync } = useCheckAddressForExpressMutation();
  const { mode, expressPointId, toggleMode, setExpressPointId, resetExpressPointId } = useMarketMode();
  const { setIsAddressModalOpen, isAddressModalOpen } = useCustomerAddressesStateData();
  const { isAddressSelectOpen, setIsAddressSelectOpen } = useCustomerAddressSelectData();
  const { unAuthedCustomerAddresses, currentShippingAddressId, setCurrentShippingAddressId } =
    useCustomerAddressesStorageData();
  const isViewFocused = useIsViewFocused();
  const { getProductsTotalWeight } = useCartData();
  const totalWeight = getProductsTotalWeight();
  const { activeShippingZone } = useActiveOrMainShippingZone();

  const { data: customerAddresses } = useGetCustomerAddresses({
    customerId: user?.id,
    options: {
      enabled: !!user?.id,
    },
  });

  const filterAdresses = <T extends { city?: string | null }[]>(addrs: T): T => {
    if (isPathnameWithCondition) {
      const currentZoneName = activeShippingZone?.name ?? "";

      return addrs.filter((a) => a.city?.includes(currentZoneName)) as T;
    }

    return addrs;
  };

  const handleExpressAddressChange = async (newAddress: Address) => {
    if (!newAddress) return;

    if (newAddress.longitude && newAddress.latitude) {
      const isCurrentAddressAndNewAddressSame =
        address?.latitude === newAddress.latitude && address?.longitude === newAddress.longitude;

      if (!isCurrentAddressAndNewAddressSame && config.isExpressDeliveryEnabled) {
        const result = await mutateAsync({
          latitude: newAddress.latitude,
          longitude: newAddress.longitude,
          totalWeight,
        });

        const resultExpressPointId = result.expressPoints?.[0]?.id;

        // updateOrderData({
        //   address: { ...newAddress, expressPointId: resultExpressPointId },
        // });

        if (!resultExpressPointId) {
          toggleMode("market", activeShippingZone.id);
          resetExpressPointId();

          if (mode === "express") {
            // toast.warn("Экспресс доставка недоступна для вашего адреса");
            toast.warn(t("Express delivery is not available here"));
          }
        } else if (resultExpressPointId !== expressPointId) {
          setExpressPointId(resultExpressPointId);
          // if (mode === "express") {
          //   toast.success("У нового адреса также есть экспресс доставка");
          // }
        }
      }
    }
  };

  const handleDismiss = () => {
    setIsAddressSelectOpen?.(false);
  };

  const handleNewAddressCreated = (address: Address) => {
    if (isPathnameWithCondition) {
      if (activeShippingZone && address.city?.includes(activeShippingZone.name)) {
        handleExpressAddressChange(address);
      }
    } else if (!isPathnameWithCondition) {
      handleExpressAddressChange(address);
    }
  };

  const address = useGetCurrentAddress();

  const handleChangeSelectAddress = (e: any) => {
    // TBD: fix types
    const addressIdOrAddNew = e.detail.value;

    if (!addressIdOrAddNew) {
      return;
    }

    if (addressIdOrAddNew === "ADD_NEW") {
      history.replace("/home3");
      setIsAddressModalOpen(true);
      return;
    }

    setCurrentShippingAddressId(addressIdOrAddNew, "CurrentShippingAddressSelect");

    if (isPathnameWithCondition && address) {
      if (activeShippingZone && address.city && !address.city?.includes(activeShippingZone.name)) {
        handleExpressAddressChange(address);
      }
    } else if (!isPathnameWithCondition) {
      address && handleExpressAddressChange(address);
    }
  };

  return (
    <div
      onClick={() => {
        if (mode === "express" && isPathnameWithCondition) {
          toast.warn(t("To change the address, go to the main page"));
        }
      }}
    >
      <Select
        onDismiss={handleDismiss}
        isAddressSelectOpen={isAddressSelectOpen}
        disabled={mode === "express" && isPathnameWithCondition}
        value={currentShippingAddressId}
        isValid={true}
        placeholder={t("Select delivery address")}
        selectClassName={cx("select", selectColor)}
        onChange={handleChangeSelectAddress}
      >
        {!isUserAuthenticated
          ? filterAdresses(unAuthedCustomerAddresses).map((address) => {
              return (
                <IonSelectOption
                  key={address.id}
                  value={address.id}
                  disabled={
                    isPathnameWithCondition && activeShippingZone && !address.city?.includes(activeShippingZone.name)
                  }
                >
                  {getCustomerFullAddress(address)}
                </IonSelectOption>
              );
            })
          : filterAdresses(customerAddresses ?? []).map((address) => {
              return (
                <IonSelectOption
                  key={address.id}
                  value={address.id}
                  disabled={
                    isPathnameWithCondition && activeShippingZone && !address.city?.includes(activeShippingZone.name)
                  }
                >
                  {getCustomerFullAddress(address)}
                </IonSelectOption>
              );
            })}
        <IonSelectOption value="ADD_NEW" className={"shippingAddress_deliverySelectOption"}>
          {t("Add new Address")}
        </IonSelectOption>
      </Select>
      <ShippingAddressManually
        onNewAddressCreated={handleNewAddressCreated}
        onDismiss={() => {
          setIsAddressModalOpen(false);
        }}
        isOpen={isAddressModalOpen && isViewFocused}
      />
    </div>
  );
};

export { CurrentShippingAddressSelect };
